// FLAGS: personal-data, consent, silent-failure
// The app's one Application Insights instance: SDK config, the two telemetry initializers every event
// passes through, the authenticated-user link, and the trace headers axios forwards to the API.
// Everything this browser sends to Microsoft leaves from here.
import type { ComponentType } from "react";
import type { ITelemetryItem } from "@microsoft/applicationinsights-web";
import {
  ReactPlugin,
  withAITracking,
} from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { env } from "@src/env";
import { hasConsentFor } from "@src/utils/cookiebot";
import { AppInsightEvent } from "@src/appInsights/appInsights.types";
import { getEmailDomain } from "@src/utils/piiRedactor";
import sessionStorageService from "@src/services/sessionStorage.service";

export interface TraceHeaders {
  traceParent: string;
  requestId: string;
}

const reactPlugin = new ReactPlugin();
// FLAGS: personal-data, consent
// `loadAppInsights()` below is unconditional, so telemetry starts flowing whatever the cookie banner
// says; the two consent-derived options are read once, at module load, while the Cookiebot script is
// still being fetched, so both evaluate to "no consent" on first paint. `enableAutoRouteTracking`
// sends the route URL, which on the change-language route carries a refresh token.
const ai = new ApplicationInsights({
  config: {
    connectionString: env.REACT_APP_APPINSIGHTS_CONNECTION_STRING,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    correlationHeaderExcludedDomains: [
      "*.customer.io",
      "login.openup*",
      "register.openup*",
      "streaming-*.azurefd.net",
    ],
    isRetryDisabled: true,
    disableCookiesUsage: !hasConsentFor("statistics"),
    enableSessionStorageBuffer: hasConsentFor("statistics"),
  },
});
ai.loadAppInsights();

// FLAGS: consent
// Turns the cookie manager back on and sends one page view when statistics consent arrives. The config
// object above is never re-read, so the sessionStorage buffer stays off for the rest of the page's life.
if (globalThis.window !== undefined) {
  globalThis.window.addEventListener("CookiebotOnAccept", () => {
    if (hasConsentFor("statistics")) {
      ai.getCookieMgr().setEnabled(true);
      ai.trackPageView();
    }
  });
}

// FLAGS: personal-data
// Appends the current pathname to the name of every telemetry item, not only page views, so a URL that
// happens to carry an identifier is copied into metric and exception names as well.
const telemetryVersionInitializer = (envelope: ITelemetryItem): void => {
  if (envelope.data) {
    envelope.data.version = env.REACT_APP_VERSION;
  }
  if (envelope.baseData) {
    envelope.baseData.name += ` - ${globalThis.window.location.pathname}`;
  }
  envelope.tags = envelope.tags || [];
  envelope.tags["ai.cloud.role"] = "OpenUp.Web";
};
ai.addTelemetryInitializer(telemetryVersionInitializer);

const userSessionIdInitializer = (envelope: ITelemetryItem): void => {
  const userSessionId = sessionStorageService.getUserSessionIdFromStorage();
  if (userSessionId && envelope.data) {
    envelope.data.UserSessionId = userSessionId;
  }
};
ai.addTelemetryInitializer(userSessionIdInitializer);

const ComponentWithTracking = <P extends object>(
  Component: ComponentType<P>,
): ComponentType<P> => withAITracking(reactPlugin, Component);

// FLAGS: personal-data
// Attaches the OpenUp user id to every later event and stores it in a cookie, so from login onwards the
// telemetry stream is attributable to one member; only the clear below undoes it.
const setAuthenticatedUserContext = (userId: string): void => {
  ai.setAuthenticatedUserContext(userId, "", true);
};

const clearAuthenticatedUserContext = (): void => {
  ai.clearAuthenticatedUserContext();
};

const isValidSpanId = (id: string): boolean => /^[0-9a-f]{16}$/.test(id);

const generateSpanId = (): string => {
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
};

const getSpanId = (): string => {
  const parentId = ai.context?.telemetryTrace?.parentID;
  return parentId && isValidSpanId(parentId) ? parentId : generateSpanId();
};

const getTraceHeaders = (): TraceHeaders | undefined => {
  const traceId = ai.context?.telemetryTrace?.traceID;
  if (!traceId) return undefined;

  const spanId = getSpanId();
  return {
    traceParent: `00-${traceId}-${spanId}-01`,
    requestId: `|${traceId}.${spanId}.`,
  };
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) return error.message;
  if (error !== undefined) return String(error);
  return undefined;
};

// FLAGS: personal-data, silent-failure
// Attaches the signed-in user's email domain to every call, so each exception names their employer. The
// surrounding catch drops a send failure without a trace, and for flows where this event is the only
// record that something failed, nothing is left behind.
const trackException = (event: AppInsightEvent, error?: unknown) => {
  try {
    ai.appInsights.trackException({
      exception: error instanceof Error ? error : undefined,
      properties: {
        event,
        emailDomain: getEmailDomain(sessionStorageService.getUserEmail()),
        errorMessage: getErrorMessage(error),
      },
    });
  } catch {
    // silently fail
  }
};

export default ComponentWithTracking;
export const { appInsights } = ai;
export {
  reactPlugin,
  setAuthenticatedUserContext,
  clearAuthenticatedUserContext,
  trackException,
  getTraceHeaders,
};
